import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Users, Shield, ShieldCheck, User, Trash, CaretDown, Key } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Full access, manage users', icon: ShieldCheck, color: 'text-[#FF5A00]' },
  { value: 'assistant', label: 'Assistant', desc: 'Unlimited free access', icon: Shield, color: 'text-[#0047FF]' },
  { value: 'user', label: 'User', desc: 'Standard user, plan limits apply', icon: User, color: 'text-[#5E5E5E]' },
];

export default function AdminPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleMenuOpen, setRoleMenuOpen] = useState(null);
  const [passwordModal, setPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Admin access required');
      else toast.error('Failed to load users');
    } finally { setLoading(false); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      setRoleMenuOpen(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.put(`/admin/users/${userId}/password`, { new_password: newPassword });
      toast.success('Password reset successfully');
      setPasswordModal(null);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const getRoleInfo = (role) => ROLES.find(r => r.value === role) || ROLES[2];

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-black/5 w-48" />
          <div className="h-64 bg-white border border-black/15" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="admin-page">
      <div className="mb-8">
        <p className="font-mono text-xs font-bold tracking-[0.2em] uppercase text-[#FF5A00]">Administration</p>
        <h1 className="font-sans text-3xl md:text-4xl font-black tracking-tight mt-1">User Management</h1>
        <p className="font-mono text-sm text-[#5E5E5E] mt-1">{users.length} registered users</p>
      </div>

      {/* Role Legend */}
      <div className="flex flex-wrap gap-4 mb-6">
        {ROLES.map(r => (
          <div key={r.value} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-black/10">
            <r.icon size={14} weight="bold" className={r.color} />
            <span className="font-mono text-xs">
              <strong>{r.label}</strong> — {r.desc}
            </span>
          </div>
        ))}
      </div>

      {/* Users Table */}
      <div className="bg-white border border-black/15" data-testid="users-table">
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 p-4 bg-[#F7F7F7] border-b border-black/15 font-mono text-xs uppercase tracking-wider font-bold">
          <span>User</span>
          <span>Role</span>
          <span>Plan</span>
          <span>Usage</span>
          <span>Actions</span>
        </div>
        {users.map(u => {
          const roleInfo = getRoleInfo(u.role || 'user');
          const isCurrentUser = u.user_id === currentUser?.user_id;
          const sub = u.subscription;
          return (
            <div key={u.user_id} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 items-center p-4 border-b border-black/10 hover:bg-[#F7F7F7] transition-colors" data-testid={`user-row-${u.user_id}`}>
              {/* User Info */}
              <div className="flex items-center gap-3 min-w-0">
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-8 h-8 object-cover border border-black/15" />
                ) : (
                  <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-mono text-xs uppercase">
                    {u.name?.[0] || 'U'}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-mono text-sm font-medium truncate">
                    {u.name}
                    {isCurrentUser && <span className="ml-2 px-1.5 py-0.5 bg-[#FF5A00]/10 text-[#FF5A00] text-[10px] uppercase">You</span>}
                  </p>
                  <p className="font-mono text-[10px] text-[#8F8F8F] truncate">{u.email}</p>
                </div>
              </div>

              {/* Role */}
              <div className="relative">
                <button
                  onClick={() => setRoleMenuOpen(roleMenuOpen === u.user_id ? null : u.user_id)}
                  disabled={isCurrentUser}
                  data-testid={`role-btn-${u.user_id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono text-xs uppercase tracking-wider transition-colors ${
                    isCurrentUser ? 'border-black/10 text-[#8F8F8F] cursor-default' : 'border-black/20 hover:border-black cursor-pointer'
                  }`}
                >
                  <roleInfo.icon size={14} weight="bold" className={roleInfo.color} />
                  {u.role || 'user'}
                  {!isCurrentUser && <CaretDown size={10} />}
                </button>
                {roleMenuOpen === u.user_id && !isCurrentUser && (
                  <div className="absolute z-20 top-full left-0 mt-1 bg-white border border-black/15 shadow-[4px_4px_0px_rgba(0,0,0,0.1)] w-48">
                    {ROLES.map(r => (
                      <button
                        key={r.value}
                        onClick={() => handleRoleChange(u.user_id, r.value)}
                        data-testid={`set-role-${r.value}-${u.user_id}`}
                        className={`flex items-center gap-2 w-full px-3 py-2 text-left font-mono text-xs hover:bg-[#F7F7F7] transition-colors ${
                          (u.role || 'user') === r.value ? 'bg-[#F7F7F7] font-bold' : ''
                        }`}
                      >
                        <r.icon size={14} weight="bold" className={r.color} />
                        <div>
                          <p className="font-bold">{r.label}</p>
                          <p className="text-[10px] text-[#8F8F8F]">{r.desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Plan */}
              <span className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                (u.role === 'admin' || u.role === 'assistant') ? 'bg-[#FF5A00]/10 text-[#FF5A00]' :
                sub?.plan_id === 'free' ? 'bg-black/5 text-[#8F8F8F]' :
                'bg-[#00A859]/10 text-[#00A859]'
              }`}>
                {(u.role === 'admin' || u.role === 'assistant') ? 'Unlimited' : (sub?.plan_name || 'Free')}
              </span>

              {/* Usage */}
              <span className="font-mono text-xs text-[#5E5E5E]">
                {(u.role === 'admin' || u.role === 'assistant') ? 'Unlimited' : `${sub?.analyses_used || 0}/${sub?.analyses_limit || 3}`}
              </span>

              {/* Actions */}
              <div className="flex items-center gap-1">
                {!isCurrentUser && (
                  <>
                    <button
                      onClick={() => { setPasswordModal(u); setNewPassword(''); }}
                      data-testid={`reset-pw-${u.user_id}`}
                      title="Reset password"
                      className="p-1.5 text-[#8F8F8F] hover:text-[#0047FF] transition-colors"
                    >
                      <Key size={16} weight="regular" />
                    </button>
                    <button
                      onClick={() => handleDelete(u.user_id, u.name)}
                      data-testid={`delete-user-${u.user_id}`}
                      title="Delete user"
                      className="p-1.5 text-[#8F8F8F] hover:text-[#FF2B2B] transition-colors"
                    >
                      <Trash size={16} weight="regular" />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Password Reset Modal */}
      {passwordModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="reset-password-modal">
          <div className="bg-white border border-black/15 w-full max-w-md">
            <div className="p-5 border-b border-black/15">
              <h3 className="font-sans text-lg font-bold tracking-tight">Reset Password</h3>
              <p className="font-mono text-xs text-[#8F8F8F] mt-1">{passwordModal.name} ({passwordModal.email})</p>
            </div>
            <div className="p-5">
              <label className="block font-mono text-xs uppercase tracking-wider text-[#5E5E5E] mb-2">New Password</label>
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password-input"
                className="w-full bg-transparent border-b border-black/30 focus:border-black px-0 py-3 font-mono text-sm focus:outline-none transition-colors"
                placeholder="Min 6 characters"
                autoFocus
              />
              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={() => handleResetPassword(passwordModal.user_id)}
                  data-testid="confirm-reset-pw-btn"
                  className="flex-1 py-3 bg-[#FF5A00] text-white font-mono text-sm uppercase tracking-wide hover:bg-[#E04E00] transition-colors"
                >
                  Reset Password
                </button>
                <button
                  onClick={() => { setPasswordModal(null); setNewPassword(''); }}
                  data-testid="cancel-reset-pw-btn"
                  className="flex-1 py-3 border border-black text-black font-mono text-sm uppercase tracking-wide hover:bg-black hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
